// Constants for card keys to ensure consistency across the application
export const CARD_KEYS = {
  // Taiwan Shin Bank cards
  TAISHIN_JKOS: 'taishin-jkos',
  TAISHIN_FLYGO: 'taishin-flygo',
  TAISHIN_GIVING: 'taishin-giving',
  TAISHIN_SUNROSE: 'taishin-sunrose',
  TAISHIN_GOGORO: 'taishin-gogoro',
  TAISHIN_GOGO: 'taishin-gogo',
  TAISHIN_BUSINESS: 'taishin-business',
  TAISHIN_TZUKUANG: 'taishin-tzukuang',

  // Cathay Bank cards
  CATHAY_CUBE: 'cathay-cube',
  CATHAY_SHOPEE: 'cathay-shopee',
  CATHAY_ASIA_MILES: 'cathay-asia-miles',
  CATHAY_EVA: 'cathay-eva',
  CATHAY_CASH_BACK: 'cathay-cash-back',
  CATHAY_BUSINESS: 'cathay-business',

  // CTBC Bank cards
  CTBC_LINEPAY: 'ctbc-linepay',
  CTBC_TITANIUM: 'ctbc-titanium',
  CTBC_CPCYOYO: 'ctbc-cpcyoyo',
  CTBC_LALAPORT: 'ctbc-lalaport',
  CTBC_AGODA: 'ctbc-agoda',
  CTBC_ANA: 'ctbc-ana',
  CTBC_CHINA_AIRLINES: 'ctbc-china-airlines',

  // E.SUN Bank cards
  ESUN_PI: 'esun-pi',
  ESUN_UBEAR: 'esun-ubear',
  ESUN_KUMAMON: 'esun-kumamon',
  ESUN_UNICARD: 'esun-unicard',
  ESUN_STARLUX_TITANIUM: 'esun-starlux-titanium',
  ESUN_STARLUX_BUSINESS: 'esun-starlux-business',
  ESUN_STARLUX_WORLD: 'esun-starlux-world',
  ESUN_STARLUX_WORLD_SUPREME: 'esun-starlux-world-supreme',

  // Fubon Bank cards
  FUBON_J: 'fubon-j',
  FUBON_DIGITAL: 'fubon-digital',
  FUBON_MOMO: 'fubon-momo',
  FUBON_LIFESTYLE: 'fubon-lifestyle',
  FUBON_COSTCO: 'fubon-costco',
  FUBON_INSURANCE: 'fubon-insurance',

  // Sinobank cards
  SINO_DAWHO: 'sino-dawho',
  SINO_WBFU: 'sino-wbfun',
  SINO_SPORT: 'sino-sport',
  SINO_GREEN: 'sino-green',
  SINO_DUE_CURRENCY: 'sino-due-currency',
  SINO_CASHCARD_JCB: 'sino-cashcard-jcb',
  SINO_MITSUI: 'sino-mitsui',
  SINO_TAXI: 'sino-taxi',
  SINO_DAWAY: 'sino-daway',

  // DBS cards
  DBS_ECO: 'dbs-eco',
  DBS_FUNLIFE: 'dbs-funlife',
  DBS_TRAVEL_WORLD: 'dbs-travelworld',
  DBS_TRAVEL_WORLD_ELITE: 'dbs-travel-world-elite',

  // UBOT cards
  UBOT_JIHO: 'ubot-jiho',
  UBOT_MCARD: 'ubot-mcard',
  UBOT_LIANDIAN: 'ubot-liandian',
  UBOT_LINEBANK: 'ubot-linebank',

  // American Express cards
  AMERICANEXPRESS_PLAT: 'americanexpress-plat',
  AMERICANEXPRESS_GOLD: 'americanexpress-gold',
  AMERICANEXPRESS_CREDIT_PLAT: 'americanexpress-credit-plat',
  AMERICANEXPRESS_EVERGREEN: 'americanexpress-evergreen',
  AMERICANEXPRESS_JINHWA: 'americanexpress-jinhwa',

  // HSBC cards
  HSBC_CASHBACK: 'hsbc-cashback',
  HSBC_TRAVEL_INFINITE: 'hsbc-travel-infinite',
  HSBC_TRAVEL_ONE: 'hsbc-travel-one',
  HSBC_TRAVEL_ONE_SIGNATURE: 'hsbc-travel-one-signature',
  HSBC_DIAMOND: 'hsbc-diamond',
} as const;

// Constants for card category keys to ensure consistency across the application
export const CATEGORY_KEYS = {
  OVERSEAS: 'overseas',
  CASHBACK: 'cashback',
  ONLINE_SHOPPING: 'online-shopping',
  DINING: 'dining',
  TRANSPORTATION: 'transportation',
  TRAVEL: 'travel',
  MOBILE_PAYMENT: 'mobile-payment',
  ENTERTAINMENT: 'entertainment',
  MILEAGE: 'mileage',
  AIRPORT_SERVICE: 'airport-service',
  DAILY_LIFE: 'daily-life',
  INSURANCE: 'insurance',
  NO_FEE: 'no-fee',
} as const;

// Type for category keys
export type CategoryKeyType = keyof typeof CATEGORY_KEYS;

// Type for card keys to provide better type safety
export type CardKeyType = keyof typeof CARD_KEYS;

// Centralized display names for all categories
export const CATEGORY_NAMES = {
  [CATEGORY_KEYS.OVERSEAS]: '海外消費',
  [CATEGORY_KEYS.CASHBACK]: '現金回饋',
  [CATEGORY_KEYS.ONLINE_SHOPPING]: '網購',
  [CATEGORY_KEYS.DINING]: '餐飲消費',
  [CATEGORY_KEYS.TRANSPORTATION]: '交通',
  [CATEGORY_KEYS.TRAVEL]: '旅遊訂房',
  [CATEGORY_KEYS.MOBILE_PAYMENT]: '行動支付',
  [CATEGORY_KEYS.ENTERTAINMENT]: '娛樂/影音平台',
  [CATEGORY_KEYS.MILEAGE]: '里程',
  [CATEGORY_KEYS.AIRPORT_SERVICE]: '機場服務',
  [CATEGORY_KEYS.DAILY_LIFE]: '日常生活',
  [CATEGORY_KEYS.INSURANCE]: '保險',
  [CATEGORY_KEYS.NO_FEE]: '免年費',
} as const;

// Constants for channel keys to ensure consistency across the application
export const CHANNEL_KEYS = {
  // Online Shopping
  ONLINE_SHOPPING: 'online-shopping',
  SHOPEE: 'shopee',
  MOMO: 'momo',
  PCHOME: 'pchome',
  COUPANG: 'coupang',

  // Travel
  TRAVEL: 'travel',
  KKDAY: 'kkday',
  KLOOK: 'klook',
  AGODA: 'agoda',
  BOOKING: 'booking',
  MILEAGE: 'mileage',
  HOTEL: 'hotel',
  GETYOURGUIDE: 'getyourguide',

  // Dining
  DINING: 'dining',
  FOOD_DELIVERY: 'food_delivery',
  RESTAURANT: 'restaurant',
  CAFE: 'cafe',

  // Transportation
  TRANSPORTATION: 'transportation',
  GAS_STATION: 'gas-station',
  SHARED_SCOOTER: 'shared-scooter',
  TAIWAN_HIGH_SPEED_RAILWAY: 'thsrc',
  TAIWAN_RAILWAY: 'tw-railway',

  // Mobile Payment
  MOBILE_PAYMENT: 'mobile-payment',
  LINE_PAY: 'line-pay',
  APPLE_PAY: 'apple-pay',
  JKOPAY: 'jkopay',
  PXPAYPLUS: 'pxpayplus',
  TAIWAN_PAY: 'tw-pay',
  TAISHIN_PAY: 'taishin-pay',
  DIGITAL_BANKING: 'digital-banking',

  // Entertainment
  ENTERTAINMENT: 'entertainment',
  STREAMING: 'streaming',
  MOVIE: 'movie',

  // Overseas
  OVERSEAS: 'overseas',
  JAPAN: 'japan',
  THAILAND: 'thailand',
  KOREA: 'korea',
  SOUTHEAST_ASIA: 'southeast-asia',
  AMERICA: 'america',
  UK: 'uk',
  EUROPE: 'europe',

  // Daily Life
  DAILY_LIFE: 'daily-life',
  CONVENIENCE_STORE: 'convenience-store',
  DEPARTMENT_STORE: 'department-store',
  SUPERMARKET: 'supermarket',
  WAREHOUSE_CLUB: 'warehouse-club',
  BUSINESS: 'business',
  DIGITAL_CHANNEL: 'digital-channel',
  AIRPORT_LOUNGE: 'airport-lounge',
  MALL: 'mall',
  TAX: 'tax',
  INSURANCE: 'insurance',
  GYM: 'gym',
  NO_FEE: 'no-fee',
} as const;

// Type for channel keys
export type ChannelKeyType = keyof typeof CHANNEL_KEYS;

// Centralized display names for all channels
export const CHANNEL_NAMES = {
  // Online Shopping
  [CHANNEL_KEYS.ONLINE_SHOPPING]: '網購',
  [CHANNEL_KEYS.SHOPEE]: '蝦皮',
  [CHANNEL_KEYS.MOMO]: 'momo',
  [CHANNEL_KEYS.PCHOME]: 'PChome',
  [CHANNEL_KEYS.COUPANG]: 'Coupang',

  // Travel
  [CHANNEL_KEYS.TRAVEL]: '旅遊',
  [CHANNEL_KEYS.KKDAY]: 'KKday',
  [CHANNEL_KEYS.KLOOK]: 'Klook',
  [CHANNEL_KEYS.AGODA]: 'Agoda',
  [CHANNEL_KEYS.BOOKING]: 'Booking.com',
  [CHANNEL_KEYS.MILEAGE]: '哩程',
  [CHANNEL_KEYS.HOTEL]: '飯店',
  [CHANNEL_KEYS.GETYOURGUIDE]: 'GetYourGuide',

  // Dining
  [CHANNEL_KEYS.DINING]: '餐飲',
  [CHANNEL_KEYS.FOOD_DELIVERY]: '外送',
  [CHANNEL_KEYS.RESTAURANT]: '餐廳',
  [CHANNEL_KEYS.CAFE]: '咖啡',

  // Transportation
  [CHANNEL_KEYS.TRANSPORTATION]: '交通',
  [CHANNEL_KEYS.GAS_STATION]: '加油',
  [CHANNEL_KEYS.SHARED_SCOOTER]: '共享機車',
  [CHANNEL_KEYS.TAIWAN_HIGH_SPEED_RAILWAY]: '高鐵',

  // Mobile Payment
  [CHANNEL_KEYS.MOBILE_PAYMENT]: '行動支付',
  [CHANNEL_KEYS.LINE_PAY]: 'LINE Pay',
  [CHANNEL_KEYS.APPLE_PAY]: 'Apple Pay',
  [CHANNEL_KEYS.JKOPAY]: '街口支付',
  [CHANNEL_KEYS.PXPAYPLUS]: '全支付',
  [CHANNEL_KEYS.DIGITAL_BANKING]: '數位銀行',
  [CHANNEL_KEYS.TAIWAN_PAY]: '台灣Pay',
  [CHANNEL_KEYS.TAISHIN_PAY]: '台新Pay',

  // Entertainment
  [CHANNEL_KEYS.ENTERTAINMENT]: '娛樂',
  [CHANNEL_KEYS.STREAMING]: '串流',
  [CHANNEL_KEYS.MOVIE]: '電影',

  // Overseas
  [CHANNEL_KEYS.OVERSEAS]: '海外',
  [CHANNEL_KEYS.JAPAN]: '日本',
  [CHANNEL_KEYS.THAILAND]: '泰國',
  [CHANNEL_KEYS.KOREA]: '韓國',
  [CHANNEL_KEYS.SOUTHEAST_ASIA]: '東南亞',
  [CHANNEL_KEYS.EUROPE]: '歐洲',
  [CHANNEL_KEYS.AMERICA]: '美洲',

  // Daily Life
  [CHANNEL_KEYS.DAILY_LIFE]: '日常生活',
  [CHANNEL_KEYS.CONVENIENCE_STORE]: '便利商店',
  [CHANNEL_KEYS.DEPARTMENT_STORE]: '百貨公司',
  [CHANNEL_KEYS.SUPERMARKET]: '超市',
  [CHANNEL_KEYS.WAREHOUSE_CLUB]: '量販店',
  [CHANNEL_KEYS.BUSINESS]: '商務',
  [CHANNEL_KEYS.DIGITAL_CHANNEL]: '數位通路',
  [CHANNEL_KEYS.AIRPORT_LOUNGE]: '機場貴賓室',
  [CHANNEL_KEYS.GYM]: '健身房',
  [CHANNEL_KEYS.TAX]: '稅',
  [CHANNEL_KEYS.INSURANCE]: '保險',
  [CHANNEL_KEYS.NO_FEE]: '免年費',
} as const;

export const BANK_KEYS = {
  TAISHIN: 'taishin',
  CATHAY: 'cathay',
  CTBC: 'ctbc',
  ESUN: 'esun',
  FUBON: 'fubon',
  SINO: 'sino',
  DBS: 'dbs',
  UBOT: 'ubot',
  AMERICANEXPRESS: 'americanexpress',
  HSBC: 'hsbc',
} as const;

// Constants for issuing banks to ensure consistency
export const BANK_NAMES = {
  [BANK_KEYS.TAISHIN]: '台新銀行 Taishin Bank',
  [BANK_KEYS.CATHAY]: '國泰世華銀行 Cathay United Bank',
  [BANK_KEYS.CTBC]: '中國信託銀行 CTBC Bank',
  [BANK_KEYS.ESUN]: '玉山銀行 E.Sun Bank',
  [BANK_KEYS.FUBON]: '富邦銀行 Fubon Bank',
  [BANK_KEYS.SINO]: '永豐銀行 Sinobank',
  [BANK_KEYS.DBS]: '星展銀行 DBS Bank',
  [BANK_KEYS.UBOT]: '聯邦銀行 Ubot Bank',
  [BANK_KEYS.AMERICANEXPRESS]: '美國運通 American Express',
  [BANK_KEYS.HSBC]: '滙豐銀行 HSBC',
} as const;
